<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: true,
  },
  verified: {
    type: Boolean,
    default: false,
  },
  quiet: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open', 'action']);

const { t } = useI18n();
const [showMenu, toggleMenu] = useToggle();

const statusLabel = computed(() => {
  if (!props.enabled) return t('ONIX.ACTIONS.PAUSED');
  if (props.verified) return t('ONIX.ACTIONS.READY');
  return t('ONIX.ACTIONS.NEEDS_TRY');
});

const statusClass = computed(() => {
  if (!props.enabled) return 'text-n-slate-11 bg-n-alpha-2';
  if (props.verified) return 'text-n-teal-11 bg-n-teal-3';
  return 'text-n-amber-11 bg-n-amber-3';
});

const menuItems = computed(() => [
  {
    label: t('ONIX.ACTIONS.EDIT'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('ONIX.ACTIONS.DELETE'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const handleAction = ({ action }) => {
  toggleMenu(false);
  emit('action', { action, id: props.id });
};
</script>

<template>
  <CardLayout class="cursor-pointer" @click="emit('open')">
    <div class="flex items-start justify-between w-full gap-3">
      <div class="min-w-0">
        <div class="text-base font-medium text-n-slate-12 line-clamp-1">
          {{ title }}
        </div>
        <p
          v-if="description"
          class="m-0 mt-1 text-sm text-n-slate-11 line-clamp-2"
        >
          {{ description }}
        </p>
      </div>
      <div class="flex items-center gap-2 shrink-0" @click.stop>
        <span class="text-xs px-2 py-0.5 rounded-full" :class="statusClass">
          {{ statusLabel }}
        </span>
        <Policy
          v-if="!quiet"
          v-on-clickaway="() => toggleMenu(false)"
          :permissions="['administrator']"
          class="relative"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            variant="ghost"
            @click="toggleMenu()"
          />
          <DropdownMenu
            v-if="showMenu"
            :menu-items="menuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full"
            @action="handleAction"
          />
        </Policy>
      </div>
    </div>
  </CardLayout>
</template>
